<script lang="ts">
  import { onMount } from "svelte";
  import { fly, fade, slide } from "svelte/transition";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { Button } from "flowbite-svelte";
  import { logError, logInfo, logWarning } from "../../../services/logging/log-handler.js";

  function autofocus(node: HTMLInputElement) {
    node.focus();
    return {
      destroy() {},
    };
  }
  import { faChevronDown, faChevronRight, faPencil, faTrash, faCheck, faXmark, faPenToSquare} from "@fortawesome/free-solid-svg-icons";
  import { userStore } from "../../../services/stores/user-store.js";
  import { get } from "svelte/store";
  import { api } from "../../../services/api/api-client.js";
  import { createEventDispatcher } from "svelte";
  import { chatSessionStore } from "../../../services/stores/chat-session-store.js";
  import { newChatSignal } from "../../../services/stores/new-chat-signal-store.js";

  const dispatch = createEventDispatcher();

  interface ChatSession {
    chat_session_id: string;
    name: string;
    started: string;
    message_count: number;
    last_message_time?: string;
  }

  interface ChatQA {
    chat_qa_id: string;
    request: any;
    response: any;
    sent: string;
    response_time?: number;
  }

  type TimePeriod = "Today" | "Yesterday" | "This Week" | "Last Week" | "Last Month" | "Earlier";

  let sessions: ChatSession[] = [];
  let sessionQA: Map<string, ChatQA[]> = new Map();
  let isLoading = false;
  let object_id: string | null = null;

  // Accordion states - time periods expanded by default, sessions collapsed by default
  let timePeriodExpanded: Record<TimePeriod, boolean> = {
    "Today": true,
    "Yesterday": true,
    "This Week": true,
    "Last Week": true,
    "Last Month": true,
    "Earlier": true,
  };

  let sessionExpanded: Record<string, boolean> = {};

  // Edit/delete states
  let editingSessionId: string | null = null;
  let editingSessionName: string = "";
  let deletingSessionId: string | null = null;
  let hoveredSessionId: string | null = null;

  // Get user object_id
  $: {
    const store = get(userStore);
    if (store.user?.oid) {
      if (object_id !== store.user.oid) {
        object_id = store.user.oid;
        if (object_id) {
          restoreExpandedState(); // Restore expanded state when object_id changes
          loadSessions();
        }
      }
    }
  }

  // SessionStorage key for expanded state
  function getStorageKey(objectId: string): string {
    return `chatHistory_expanded_${objectId}`;
  }

  // Save expanded state to sessionStorage
  function saveExpandedState() {
    if (!object_id) return;
    
    try {
      const state = {
        timePeriodExpanded,
        sessionExpanded: Object.fromEntries(Object.entries(sessionExpanded)),
      };
      const key = getStorageKey(object_id);
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error saving expanded state" });
    }
  }

  // Restore expanded state from sessionStorage
  function restoreExpandedState() {
    if (!object_id) return;
    
    try {
      const key = getStorageKey(object_id);
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const state = JSON.parse(stored);
        if (state.timePeriodExpanded) {
          timePeriodExpanded = { ...timePeriodExpanded, ...state.timePeriodExpanded };
        }
        if (state.sessionExpanded) {
          sessionExpanded = { ...sessionExpanded, ...state.sessionExpanded };
        }
      }
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error restoring expanded state" });
    }
  }

  // Also load on mount if object_id is already available
  onMount(() => {
    const store = get(userStore);
    if (store.user?.oid && !object_id) {
      object_id = store.user.oid;
      restoreExpandedState(); // Restore expanded state before loading sessions
      loadSessions();
    } else if (object_id) {
      restoreExpandedState(); // Restore if object_id is already set
    }
  });

  // Subscribe to chatSessionStore for reactivity
  $: update = $chatSessionStore;
  
  // Reactive subscription to session updates from Chatbot2
  $: if (update) {
    
    if (update.type === "nameChanged") {
      // Update session name in local state immediately without full reload
      const sessionIndex = sessions.findIndex(s => s.chat_session_id === update.sessionId);
      if (sessionIndex !== -1 && update.newName) {
        sessions[sessionIndex].name = update.newName;
        sessions = [...sessions]; // Trigger reactivity
        // No need to reload all sessions - just update the name locally
      }
    } else if (update.type === "questionAdded") {
      // Update session locally and reload Q&A without full refresh
      if (update.sessionId) {
        // Capture values before setTimeout (update will be cleared by store after 100ms)
        const sessionId = update.sessionId;
        const timestamp = update.timestamp;
        
        // Update the session's last_message_time in local state immediately
        const sessionIndex = sessions.findIndex(s => s.chat_session_id === sessionId);
        if (sessionIndex !== -1) {
          // Session exists - update locally
          // Update last_message_time to current time (or use timestamp from update if provided)
          sessions[sessionIndex].last_message_time = timestamp || new Date().toISOString();
          // Also update message_count if available
          if (sessions[sessionIndex].message_count !== undefined) {
            sessions[sessionIndex].message_count += 1;
          }
          // Create a new array to trigger reactivity and re-group by time period
          // This will smoothly move the session to the correct time period without a full reload
          sessions = [...sessions];
        } else {
          // Session doesn't exist locally - this is a new chat session
          // Reload sessions list to include the new session
          loadSessions();
        }
        // Reload Q&A with a delay to ensure backend has saved it
        // Note: We preserve the expanded/collapsed state - don't auto-expand
        setTimeout(async () => {
          await loadSessionQA(sessionId);
          // loadSessionQA already updates sessionQA and triggers reactivity
          // No need to update again - the reactive questionsBySession will update automatically
          const questionCount = (sessionQA.get(sessionId) || []).length;
        }, 1500);
      }
    } else if (update.type === "refresh") {
      // Refresh everything for this session
      if (update.sessionId) {
        loadSessionQA(update.sessionId);
        loadSessions();
      }
    }
  }

  function groupSessionsByTimePeriod(sessions: ChatSession[]): Record<TimePeriod, ChatSession[]> {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
    const lastWeek = new Date(thisWeek);
    lastWeek.setDate(thisWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(today.getMonth() - 1);

    const grouped: Record<TimePeriod, ChatSession[]> = {
      "Today": [],
      "Yesterday": [],
      "This Week": [],
      "Last Week": [],
      "Last Month": [],
      "Earlier": [],
    };

    sessions.forEach((session) => {
      // Use last_message_time if available, otherwise fall back to started
      const dateToUse = session.last_message_time || session.started;
      const sessionDate = new Date(dateToUse);
      const sessionDateOnly = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
      
      if (sessionDateOnly.getTime() === today.getTime()) {
        grouped["Today"].push(session);
      } else if (sessionDateOnly.getTime() === yesterday.getTime()) {
        grouped["Yesterday"].push(session);
      } else if (sessionDateOnly >= thisWeek) {
        // This Week: >= start of week but not today or yesterday
        grouped["This Week"].push(session);
      } else if (sessionDateOnly >= lastWeek) {
        grouped["Last Week"].push(session);
      } else if (sessionDateOnly >= lastMonth) {
        grouped["Last Month"].push(session);
      } else {
        grouped["Earlier"].push(session);
      }
    });

    // Sort sessions within each time period by last_message_time (most recent first)
    const sortSessions = (sessions: ChatSession[]) => {
      return sessions.sort((a, b) => {
        const timeA = new Date(a.last_message_time || a.started).getTime();
        const timeB = new Date(b.last_message_time || b.started).getTime();
        return timeB - timeA; // Descending order (most recent first)
      });
    };

    // Sort all time periods
    Object.keys(grouped).forEach((period) => {
      grouped[period as TimePeriod] = sortSessions(grouped[period as TimePeriod]);
    });

    return grouped;
  }

  async function loadSessions() {
    if (!object_id) {
      return;
    }

    isLoading = true;
    try {
      const response = await api.post("/chat2GetSessions", {
        object_id: object_id,
      });
      
      // The API returns { data: { sessions } }, so response.data is { data: { sessions } }
      // But api client returns JSON directly, so response is { data: { sessions } }
      // So response.data is { sessions } and response.data.sessions is the array
      const sessionsData = response.data?.sessions || response.data?.data?.sessions;
      if (sessionsData && Array.isArray(sessionsData)) {
        // Include all sessions, regardless of name
        sessions = sessionsData;
        // Load Q&A (questions) for all sessions upfront
        await Promise.all(
          sessions.map((session) => loadSessionQA(session.chat_session_id))
        );
      } else {
        sessions = [];
      }
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error loading chat sessions" });
      sessions = [];
    } finally {
      isLoading = false;
    }
  }

  async function loadSessionQA(sessionId: string) {
    if (!object_id || !sessionId) return;

    try {
      const response = await api.post("/chat2GetSessionQA", {
        object_id: object_id,
        chat_session_id: sessionId,
      });
      // Check both possible response structures
      const qaRecords = response.data?.data?.qa_records || response.data?.qa_records;
      if (qaRecords) {
        sessionQA.set(sessionId, qaRecords);
        // Trigger reactivity by creating a new Map instance
        sessionQA = new Map(sessionQA);
      }
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error loading Q&A for session " + sessionId });
    }
  }

  function toggleTimePeriod(period: TimePeriod) {
    timePeriodExpanded[period] = !timePeriodExpanded[period];
    timePeriodExpanded = { ...timePeriodExpanded };
    saveExpandedState(); // Save state when toggled
  }

  function toggleSession(sessionId: string) {
    sessionExpanded[sessionId] = !sessionExpanded[sessionId];
    sessionExpanded = { ...sessionExpanded };
    saveExpandedState(); // Save state when toggled
    
    // Q&A (questions) are already loaded for all sessions when sessions are loaded
    // No need to load on-demand anymore
  }

  function handleSessionClick(session: ChatSession) {
    // Emit event to open session in chat
    dispatch("openSession", {
      sessionId: session.chat_session_id,
      sessionName: session.name,
    });
  }

  function handleEditClick(session: ChatSession, event: MouseEvent) {
    event.stopPropagation();
    editingSessionId = session.chat_session_id;
    editingSessionName = session.name;
  }

  function handleDeleteClick(session: ChatSession, event: MouseEvent) {
    event.stopPropagation();
    deletingSessionId = session.chat_session_id;
  }

  async function handleSaveEdit() {
    if (!editingSessionId || !object_id || !editingSessionName.trim()) return;

    try {
      await api.post("/chat2RenameSession", {
        object_id: object_id,
        chat_session_id: editingSessionId,
        new_name: editingSessionName.trim(),
      });
      const savedSessionId = editingSessionId;
      const savedSessionName = editingSessionName.trim();
      
      // Update the session name locally without full reload
      const sessionIndex = sessions.findIndex(s => s.chat_session_id === savedSessionId);
      if (sessionIndex !== -1) {
        sessions[sessionIndex].name = savedSessionName;
        sessions = [...sessions]; // Trigger reactivity
      }
      
      editingSessionId = null;
      editingSessionName = "";
      
      // Notify Chatbot2 of name change
      if (savedSessionId) {
        chatSessionStore.notifyNameChanged(savedSessionId, savedSessionName);
      }
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error renaming session" });
    }
  }

  function handleCancelEdit() {
    editingSessionId = null;
    editingSessionName = "";
  }

  async function handleConfirmDelete() {
    if (!deletingSessionId || !object_id) return;

    try {
      await api.post("/chat2DeleteSession", {
        chat_session_id: deletingSessionId,
        hardDelete: false,
      });
      deletingSessionId = null;
      await loadSessions();
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error deleting session" });
    }
  }

  function handleCancelDelete() {
    deletingSessionId = null;
  }

  function truncateText(text: string, maxLength: number = 60): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  }

  function getQuestionText(qa: ChatQA): string {
    if (typeof qa.request === "string") {
      return qa.request;
    }
    if (qa.request?.question) {
      return qa.request.question;
    }
    return "Question";
  }

  function getTimePeriod(period: string): TimePeriod {
    return period as TimePeriod;
  }

  function formatTime(dateString: string): string {
    try {
      const date = new Date(dateString);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes}${ampm}`;
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error formatting time" });
      return "";
    }
  }

  function formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error formatting date" });
      return "";
    }
  }

  $: groupedSessions = groupSessionsByTimePeriod(sessions);
  
  // Reactive statement to track sessionQA changes and force re-rendering
  $: sessionQA; // This ensures the template re-renders when sessionQA changes
  
  // Helper function to get questions for a session (reactive to sessionQA changes)
  function getSessionQuestions(sessionId: string): ChatQA[] {
    // Access sessionQA to ensure reactivity
    const qa = sessionQA.get(sessionId);
    return qa || [];
  }
  
  // Reactive statement to create a string key that changes when questions are added
  // This helps force template updates when questions change
  $: sessionQAKeys = Array.from(sessionQA.keys()).join(',') + '|' + 
    Array.from(sessionQA.entries()).map(([id, qa]) => `${id}:${qa?.length || 0}`).join(',');

  // Function to handle new chat button click
  function handleNewChat() {
    newChatSignal.trigger();
  }

  // Function to delete all chat sessions
  let isDeletingAll = false;
  async function handleDeleteAllChats() {
    if (!object_id || sessions.length === 0) return;
    
    // Confirm with user
    if (!confirm(`Are you sure you want to delete all ${sessions.length} chat session(s)? This action cannot be undone.`)) {
      return;
    }

    isDeletingAll = true;
    try {
      // Delete all sessions one by one
      const deletePromises = sessions.map((session) =>
        api.post("/chat2DeleteSession", {
          chat_session_id: session.chat_session_id,
          hardDelete: false,
        }).catch((error) => {
          logError(error, "ChatHistory", undefined, { message: "Error deleting session " + session.chat_session_id });
          // Continue with other deletions even if one fails
          return null;
        })
      );

      await Promise.all(deletePromises);
      // Reload sessions after all deletions
      await loadSessions();

    } catch (error) {
      logError(error, "ChatHistory", undefined, { message: "Error deleting all sessions" });
    } finally {
      isDeletingAll = false;
    }
  }
</script>

<div class="chat-history">
  <!-- Toolbar with Delete All Chats button -->
  <div class="chat-history-toolbar">
    <!-- New Chat button hidden per user request -->
    <!-- <Button
      type="button"
      class="new-chat-button"
      aria-label="New Chat"
      title="New Chat"
      on:click={handleNewChat}
      disabled={isDeletingAll}
    >
      <FontAwesomeIcon icon={faPenToSquare} class="mr-2" />New Chat
    </Button> -->
    <Button
      type="button"
      class="delete-all-chats-button"
      aria-label="Delete All Chats"
      title="Delete All Chats"
      on:click={handleDeleteAllChats}
      disabled={isDeletingAll || sessions.length === 0}
    >
      <FontAwesomeIcon icon={faTrash} class="mr-2" />Delete All Chats
    </Button>
  </div>

  <!-- Debug: Component is rendering -->
  {#if isLoading}
    <div class="chat-history-loading">Loading chat sessions...</div>
  {:else if sessions.length === 0}
    <div class="chat-history-empty">
      <p>No chat sessions found.</p>
    </div>
  {:else}
    {#each Object.entries(groupedSessions) as [period, periodSessions]}
      {#if periodSessions.length > 0}
        {@const periodType = getTimePeriod(period)}
        {@const isExpanded = timePeriodExpanded[periodType]}
        <div class="time-period-group">
          <button
            class="time-period-header"
            on:click={() => toggleTimePeriod(periodType)}
            type="button"
          >
            {#key `${period}-${isExpanded}`}
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              class="time-period-chevron"
            />
            {/key}
            <span class="time-period-label">{period}</span>
          </button>
          {#if isExpanded}
            <div class="sessions-list">
              {#each periodSessions as session (session.chat_session_id)}
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <div
                  class="session-item"
                  on:mouseenter={() => (hoveredSessionId = session.chat_session_id)}
                  on:mouseleave={() => (hoveredSessionId = null)}
                  transition:fly={{ y: -5, duration: 250, opacity: 0.8 }}
                >
                  <div class="session-header">
                    <button class="session-toggle" on:click={() => toggleSession(session.chat_session_id)} type="button">
                      {#key `${session.chat_session_id}-${sessionExpanded[session.chat_session_id]}`}
                      <FontAwesomeIcon
                        icon={sessionExpanded[session.chat_session_id]
                          ? faChevronDown
                          : faChevronRight}
                        class="session-chevron"
                      />
                      {/key}
                    </button>
                    {#if editingSessionId === session.chat_session_id}
                      <div class="session-edit-mode">
                        <input
                          type="text"
                          class="session-edit-input"
                          bind:value={editingSessionName}
                          on:keydown={(e) => {
                            if (e.key === "Enter") handleSaveEdit();
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                          use:autofocus
                        />
                        <button class="action-button" on:click={handleSaveEdit} type="button" title="Save">
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button class="action-button" on:click={handleCancelEdit} type="button" title="Cancel">
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    {:else if deletingSessionId === session.chat_session_id}
                      <div class="session-delete-mode">
                        <span class="session-delete-label">Delete?</span>
                        <button class="action-button" on:click={handleConfirmDelete} type="button" title="Confirm">
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button class="action-button" on:click={handleCancelDelete} type="button" title="Cancel">
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    {:else}
                      <button class="session-name-button" on:click={() => handleSessionClick(session)} type="button">
                        <span class="text">{session.name || "New Chat"}</span> <span class="datetime">
                          {#if period === "Today" || period === "Yesterday"}
                            {formatTime(session.last_message_time || session.started)}
                          {:else}
                            {formatDate(session.last_message_time || session.started)}
                          {/if}
                        </span>  
                      </button>
                      {#if hoveredSessionId === session.chat_session_id}
                        <div class="session-actions">
                          <button class="action-button" on:click={(e) => handleEditClick(session, e)} type="button" title="Edit">
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button class="action-button" on:click={(e) => handleDeleteClick(session, e)} type="button" title="Delete">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      {/if}
                    {/if}
                  </div>
                  {#if sessionExpanded[session.chat_session_id]}
                    {@const questions = sessionQA.get(session.chat_session_id) || []}
                    {#key `${session.chat_session_id}-${sessionQAKeys}`}
                      <div class="session-questions" transition:slide={{ duration: 200 }}>
                        {#if sessionQA.has(session.chat_session_id)}
                          {#if questions.length > 0}
                            <ul class="list-disc list-inside">
                              {#each questions as qa, index (qa.chat_qa_id)}
                                <li class="question-item" transition:fade={{ duration: 150, delay: index * 30 }}>
                                  <span class="text">{truncateText(getQuestionText(qa))}</span>
                                </li>
                              {/each}
                            </ul>
                          {:else}
                            <div class="question-item">
                              <span class="text empty">No questions yet</span>
                            </div>
                          {/if}
                        {:else}
                          <div class="question-item">
                            <span class="text loading">Loading questions...</span>
                          </div>
                        {/if}
                      </div>
                    {/key}
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
    {/each}
  {/if}
</div>

